// src/ManageTeachers.js
import React, { useEffect, useState } from 'react';
import API from './api';
import {
  FaEdit,
  FaMapMarkerAlt,
  FaHistory,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaSave,
  FaTimes,
  FaCheck,
  FaMinus,
  FaKey,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

import UserTaxonomyEditor from './UserTaxonomyEditor';
import { saveTeacherTaxonomy } from './api';

const teacherIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const FieldError = ({ error }) =>
  error ? <span className="text-red-500 text-xs ml-1">{error}</span> : null;

const initialForm = {
  id: null,
  name: '',
  email: '',
  password: '',
  is_active: true,
  is_available: true,
  pay_first_commute: false,
  hourly_rate: '',
  travel_cost_per_km: '',
  bonus: '',
  years_experience: '',
  max_travel_miles: '',
  max_caseload: '',
  gender: 'other',
  latitude: '',
  longitude: '',
  personal_phone: '',
  personal_email: '',
  company_phone: '',
  company_email: '',
  employment_start_date: '',
  employment_end_date: '',
  admin_notes: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: '',
};

function ManageTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewInactive, setViewInactive] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTeacherForAudit, setActiveTeacherForAudit] = useState(null);
  const [mapTeacherLocation, setMapTeacherLocation] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [taxo, setTaxo] = useState({ strengths: [], limitations: [] });

  // CSV import modal state
  const [importErrors, setImportErrors] = useState([]);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(null);
  const [importFailCount, setImportFailCount] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);

  // Magic login modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectedTeacherForLogin, setSelectedTeacherForLogin] = useState(null);
  const [sendingLogin, setSendingLogin] = useState(false);

  const isEditMode = !!formData.id;

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const res = await API.get('/teachers');
      setTeachers(res.data || []);
    } catch (err) {
      toast.error('Cannot load teachers');
    }
  };

  const requiredFields = [
    'name',
    'email',
    'address_street',
    'address_city',
    'address_state',
    'address_zip',
    'address_country',
  ];

  const validate = () => {
    let errs = {};
    for (let f of requiredFields) if (!formData[f]) errs[f] = 'Required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Invalid format';
    }

    if (!formData.id && !formData.password) {
      errs.password = 'Password required';
    } else if (formData.id && formData.password && formData.password.length < 6) {
      errs.password = 'Min 6 chars';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openEditModal = async (teacher = null) => {
    setFormData(
      teacher
        ? {
            ...initialForm,
            ...teacher,
            bonus: teacher.bonus || '',
            password: '',
            pay_first_commute: !!teacher.pay_first_commute,
            years_experience: teacher.years_experience ?? '',
            max_travel_miles: teacher.max_travel_miles ?? '',
            max_caseload: teacher.max_caseload ?? '',
            gender: teacher.gender || 'other',
          }
        : initialForm
    );
    setErrors({});

    if (!teacher) {
      setTaxo({
        strengths: [{ code: 'PROG_EI', level: 'basic', weight: 1 }],
        limitations: [],
      });
    } else {
      setTaxo({ strengths: [], limitations: [] });
    }

    setModalOpen(true);

    if (teacher?.id) {
      try {
        const { data } = await API.get(`/teachers/${teacher.id}/taxonomy`);
        setTaxo({
          strengths: data?.strengths || [],
          limitations: data?.limitations || [],
        });
      } catch {
        // ignore
      }
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const strengthsNow = (taxo.strengths || []).map((s) =>
      typeof s === 'string' ? { code: s, level: 'basic', weight: 1 } : s
    );
    const hasEI = strengthsNow.some((s) => String(s.code).toUpperCase() === 'PROG_EI');
    const hasPI = strengthsNow.some((s) => String(s.code).toUpperCase() === 'PROG_PI');
    if (!hasEI && !hasPI) {
      toast.error('At least one program must be selected (EI and/or PI).');
      return;
    }

    setLoading(true);
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      data.pay_first_commute = !!data.pay_first_commute;

      const method = formData.id ? API.put : API.post;
      const url = formData.id ? `/teachers/${formData.id}` : '/teachers';
      const res = await method(url, data);

      const teacherId = formData.id ?? res.data?.id;

      if (teacherId) {
        const strengthsPayload = strengthsNow.map((x) =>
          typeof x === 'string'
            ? { code: x, level: 'basic', weight: 1 }
            : { code: x.code, level: x.level, weight: x.weight }
        );
        const limitationsPayload = (taxo.limitations || []).map((x) =>
          typeof x === 'string' ? { code: x } : { code: x.code }
        );

        try {
          await saveTeacherTaxonomy(teacherId, {
            strengths: strengthsPayload,
            limitations: limitationsPayload,
          });
        } catch {
          // ignore
        }
      }

      toast.success('Teacher saved!');
      if (!formData.id) {
        toast.info('Tip: open Edit to set Profile (Strengths / Limitations) for this teacher.');
      }

      setModalOpen(false);
      fetchTeachers();
    } catch (err) {
      if (err.response?.data?.missing) {
        let errs = {};
        for (let f of err.response.data.missing) errs[f] = 'Required';
        setErrors(errs);
      }
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveStatus = async (teacher) => {
    try {
      const storedUser = localStorage.getItem('user');
      const user_id = storedUser ? JSON.parse(storedUser).id : null;
      await API.patch(`/teachers/${teacher.id}`, {
        is_active: !teacher.is_active,
        user_id,
      });
      fetchTeachers();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const loadAuditLog = async (teacherId) => {
    try {
      const res = await API.get(`/teachers/${teacherId}/audit`);
      setAuditLog(res.data);
      setActiveTeacherForAudit(teacherId);
    } catch (err) {
      toast.error('Cannot load history');
    }
  };

  const showTeacherLocation = async (teacherId) => {
    try {
      const res = await API.get(`/teachers/${teacherId}/last-location`);
      setMapTeacherLocation({
        lat: res.data.lat,
        lon: res.data.lon,
        name: teachers.find((t) => t.id === teacherId)?.name || '',
      });
      setMapModalOpen(true);
    } catch {
      toast.info('No location found');
    }
  };

  // MAGIC LOGIN: open confirm modal
  const openSendLoginModal = (teacher) => {
    setSelectedTeacherForLogin(teacher);
    setLoginModalOpen(true);
  };

  // MAGIC LOGIN: call API
  const handleSendLoginLink = async () => {
    if (!selectedTeacherForLogin) return;
    setSendingLogin(true);
    try {
      await API.post(`/teachers/${selectedTeacherForLogin.id}/send-login-link`);
      toast.success(`Login link & QR code sent to ${selectedTeacherForLogin.email}`);
      setLoginModalOpen(false);
      setSelectedTeacherForLogin(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send login link');
    } finally {
      setSendingLogin(false);
    }
  };

  // CSV IMPORT
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported!');
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        setImportLoading(true);
        setImportProgress(0);
        setImportTotal(results.data.length);
        try {
          let success = 0;
          let failed = 0;
          let allResults = [];
          for (let i = 0; i < results.data.length; i++) {
            setImportProgress(i + 1);
            try {
              const row = { ...results.data[i] };
              if ('pay_first_commute' in row) {
                const s = String(row.pay_first_commute || '').trim().toLowerCase();
                row.pay_first_commute =
                  s === '1' || s === 'true' || s === 'yes' || s === 'da' || s === 'y'
                    ? 1
                    : 0;
              }
              const res = await API.post('/teachers/import-csv', row);
              const importResult = Array.isArray(res.data.results)
                ? res.data.results[0]
                : res.data;
              if (importResult && importResult.success) success++;
              else failed++;
              allResults.push(
                importResult || {
                  row: results.data[i],
                  success: false,
                  error: 'Unknown error',
                }
              );
            } catch (err) {
              failed++;
              allResults.push({
                row: results.data[i],
                success: false,
                error: err.response?.data?.error || err.message,
              });
            }
          }
          setImportSuccessCount(success);
          setImportFailCount(failed);
          const errors = allResults.filter((r) => !r.success);
          if (errors.length > 0) {
            setImportErrors(errors);
            setShowImportErrorModal(true);
          } else {
            setImportErrors([]);
            setShowImportErrorModal(false);
          }
          toast.success(`Imported: ${success}, Failed: ${failed}`);
          fetchTeachers();
        } catch (err) {
          toast.error('Import failed: ' + (err.response?.data?.error || err.message));
        }
        setImportLoading(false);
        setImportProgress(0);
        setImportTotal(0);
      },
    });
  };

  // CSV EXPORT
  const handleExportCSV = async () => {
    try {
      const res = await API.get('/teachers/export');
      const csv = Papa.unparse(res.data);
      const blob = new Blob([csv], { type: 'text/csv' });
      saveAs(blob, 'teachers_export.csv');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const filteredTeachers = teachers.filter((t) => t.is_active !== viewInactive);

  return (
    <div className="p-1">
      <ToastContainer position="top-center" />

      {/* Header */}
      <div className="flex justify-between items-center mb-4 gap-2">
        <h2 className="text-lg font-semibold">Manage Teachers</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="bg-green-600 text-white px-2 py-1 rounded text-xs"
          >
            Export CSV
          </button>
          <label className="bg-sky-600 text-white px-2 py-1 rounded cursor-pointer text-xs">
            Import CSV
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportCSV}
              className="hidden"
            />
          </label>
          <button
            onClick={() => openEditModal()}
            className="bg-sky-700 text-white px-2 py-1 rounded flex items-center gap-1 text-xs"
          >
            <FaPlus /> Add
          </button>
        </div>
      </div>

      {/* Import loading overlay */}
      {importLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[280px] flex flex-col items-center">
            <span className="text-lg font-semibold mb-2">Importing data...</span>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="h-4 rounded-full transition-all bg-blue-500"
                style={{
                  width: importTotal
                    ? `${Math.round((importProgress / importTotal) * 100)}%`
                    : '0%',
                }}
              />
            </div>
            <span className="text-sm text-gray-700">
              {importProgress} / {importTotal}
            </span>
            <span className="text-xs text-gray-400 mt-2">
              This may take a while due to address geocoding...
            </span>
          </div>
        </div>
      )}

      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {filteredTeachers.map((t, index) => (
          <div key={t.id} className="bg-white rounded shadow p-3 flex flex-col">
            <div className="flex justify-between">
              <div>
                <span className="font-semibold">
                  {index + 1}. {t.name}
                </span>
                <div className="text-xs">{t.email}</div>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {t.is_active ? (
                    <FaUserCheck className="text-green-600" />
                  ) : (
                    <FaUserTimes className="text-red-600" />
                  )}
                  <span className="text-xs">{t.hourly_rate}$ /h</span>
                  <span className="text-xs">{t.travel_cost_per_km}$ /mile</span>
                  <span className="text-xs">{t.bonus || 0}$ bonus</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <button onClick={() => openEditModal(t)} className="text-blue-600">
                  <FaEdit />
                </button>
                <button onClick={() => toggleActiveStatus(t)}>
                  {t.is_active ? (
                    <FaUserTimes className="text-red-600" />
                  ) : (
                    <FaUserCheck className="text-green-600" />
                  )}
                </button>
                <button onClick={() => loadAuditLog(t.id)}>
                  <FaHistory />
                </button>
                <button onClick={() => showTeacherLocation(t.id)}>
                  <FaMapMarkerAlt />
                </button>
                <button
                  onClick={() => openSendLoginModal(t)}
                  title="Send login link & QR"
                >
                  <FaKey className="text-amber-600" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full border text-xs md:text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Name</th>
              <th className="border p-2">Email</th>
              <th className="border p-2">Gender</th>
              <th className="border p-2">Active</th>
              <th className="border p-2">Available</th>
              <th className="border p-2">$/h</th>
              <th className="border p-2">$/mile</th>
              <th className="border p-2">Bonus</th>
              <th className="border p-2">Pay first commute</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTeachers.map((t, index) => (
              <tr
                key={t.id}
                className={t.is_active ? '' : 'bg-gray-100 text-gray-500'}
              >
                <td className="border p-2">
                  {index + 1}. {t.name}
                </td>
                <td className="border p-2">{t.email}</td>
                <td className="border p-2 capitalize">{t.gender || 'other'}</td>
                <td className="border p-2 text-center">
                  {t.is_active ? (
                    <FaUserCheck className="text-green-600" />
                  ) : (
                    <FaUserTimes className="text-red-600" />
                  )}
                </td>
                <td className="border p-2 text-center">
                  {t.is_available ? <FaUserCheck /> : <FaUserTimes />}
                </td>
                <td className="border p-2">{t.hourly_rate}$</td>
                <td className="border p-2">{t.travel_cost_per_km}$</td>
                <td className="border p-2">{t.bonus || 0}$</td>
                <td className="border p-2 text-center">
                  {t.pay_first_commute ? (
                    <FaCheck className="text-green-600" />
                  ) : (
                    <FaMinus className="text-gray-400" />
                  )}
                </td>
                <td className="border p-2 text-center space-x-2">
                  <button
                    onClick={() => openEditModal(t)}
                    title="Edit"
                    className="text-blue-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => toggleActiveStatus(t)}
                    title="Toggle Active"
                  >
                    {t.is_active ? (
                      <FaUserTimes className="text-red-600" />
                    ) : (
                      <FaUserCheck className="text-green-600" />
                    )}
                  </button>
                  <button
                    onClick={() => loadAuditLog(t.id)}
                    title="History"
                  >
                    <FaHistory />
                  </button>
                  <button
                    onClick={() => showTeacherLocation(t.id)}
                    title="Last Location"
                  >
                    <FaMapMarkerAlt />
                  </button>
                  <button
                    onClick={() => openSendLoginModal(t)}
                    title="Send login link & QR"
                  >
                    <FaKey className="text-amber-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white max-h-[95vh] overflow-y-auto p-6 rounded shadow w-[97%] max-w-xl">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              {formData.id ? <FaEdit /> : <FaPlus />}{' '}
              {formData.id ? 'Edit' : 'Add'} Teacher
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid md:grid-cols-2 gap-2">
                <div>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name *"
                    className={`w-full border p-2 rounded ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                  />
                  <FieldError error={errors.name} />
                </div>
                <div>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email *"
                    className={`w-full border p-2 rounded ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                  />
                  <FieldError error={errors.email} />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-700">Gender</label>
                <select
                  name="gender"
                  value={formData.gender || 'other'}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Password */}
              <div>
                <input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    formData.id
                      ? 'New password (optional)'
                      : 'Password *'
                  }
                  className={`w-full border p-2 rounded ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                />
                {formData.id && (
                  <div className="text-xs text-slate-500 mt-1">
                    Leave empty to keep existing password.
                  </div>
                )}
                <FieldError error={errors.password} />
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />{' '}
                  Active
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_available"
                    checked={formData.is_available}
                    onChange={handleChange}
                  />{' '}
                  Available
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="pay_first_commute"
                    checked={!!formData.pay_first_commute}
                    onChange={handleChange}
                  />
                  Pay first commute
                </label>
              </div>

              <div className="grid md:grid-cols-3 gap-2">
                <input
                  name="hourly_rate"
                  value={formData.hourly_rate}
                  onChange={handleChange}
                  placeholder="Hourly rate ($)"
                  className="border p-2 rounded w-full"
                />
                <input
                  name="travel_cost_per_km"
                  value={formData.travel_cost_per_km}
                  onChange={handleChange}
                  placeholder="Travel cost $/mile"
                  className="border p-2 rounded w-full"
                />
                <input
                  name="bonus"
                  value={formData.bonus}
                  onChange={handleChange}
                  placeholder="Bonus ($)"
                  className="border p-2 rounded w-full"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-2">
                <input
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleChange}
                  placeholder="Years of experience"
                  type="number"
                  min="0"
                  className="border p-2 rounded w-full"
                />
                <input
                  name="max_travel_miles"
                  value={formData.max_travel_miles}
                  onChange={handleChange}
                  placeholder="Max travel miles"
                  type="number"
                  min="0"
                  step="0.1"
                  className="border p-2 rounded w-full"
                />
                <input
                  name="max_caseload"
                  value={formData.max_caseload}
                  onChange={handleChange}
                  placeholder="Max caseload"
                  type="number"
                  min="0"
                  className="border p-2 rounded w-full"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-2">
                <input
                  name="latitude"
                  value={formData.latitude || ''}
                  onChange={handleChange}
                  placeholder="Latitude"
                  className="w-full border p-2 rounded"
                />
                <input
                  name="longitude"
                  value={formData.longitude || ''}
                  onChange={handleChange}
                  placeholder="Longitude"
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-2">
                <input
                  name="personal_phone"
                  value={formData.personal_phone}
                  onChange={handleChange}
                  placeholder="Personal phone"
                  className="w-full border p-2 rounded"
                />
                <input
                  name="personal_email"
                  value={formData.personal_email}
                  onChange={handleChange}
                  placeholder="Personal email"
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-2">
                <input
                  name="company_phone"
                  value={formData.company_phone}
                  onChange={handleChange}
                  placeholder="Company phone"
                  className="w-full border p-2 rounded"
                />
                <input
                  name="company_email"
                  value={formData.company_email}
                  onChange={handleChange}
                  placeholder="Company email"
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-2">
                <div>
                  <label className="text-sm text-gray-700">
                    Employment Start
                  </label>
                  <input
                    type="date"
                    name="employment_start_date"
                    value={formData.employment_start_date || ''}
                    onChange={handleChange}
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-700">
                    Employment End
                  </label>
                  <input
                    type="date"
                    name="employment_end_date"
                    value={formData.employment_end_date || ''}
                    onChange={handleChange}
                    className="w-full border p-2 rounded"
                  />
                </div>
              </div>

              <textarea
                name="admin_notes"
                value={formData.admin_notes}
                onChange={handleChange}
                placeholder="Admin notes"
                className="w-full border p-2 rounded"
                rows={2}
              />

              <h4 className="font-semibold mt-2">Address *</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  placeholder="Street"
                  className={`border p-2 rounded w-full ${
                    errors.address_street ? 'border-red-500' : ''
                  }`}
                />
                <input
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleChange}
                  placeholder="City"
                  className={`border p-2 rounded w-full ${
                    errors.address_city ? 'border-red-500' : ''
                  }`}
                />
                <input
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleChange}
                  placeholder="State"
                  className={`border p-2 rounded w-full ${
                    errors.address_state ? 'border-red-500' : ''
                  }`}
                />
                <input
                  name="address_zip"
                  value={formData.address_zip}
                  onChange={handleChange}
                  placeholder="ZIP"
                  className={`border p-2 rounded w-full ${
                    errors.address_zip ? 'border-red-500' : ''
                  }`}
                />
                <input
                  name="address_country"
                  value={formData.address_country}
                  onChange={handleChange}
                  placeholder="Country"
                  className={`border p-2 rounded w-full ${
                    errors.address_country ? 'border-red-500' : ''
                  }`}
                />
              </div>
              {(errors.address_street ||
                errors.address_city ||
                errors.address_state ||
                errors.address_zip ||
                errors.address_country) && (
                <div className="text-red-500 text-xs">
                  Address fields are required.
                </div>
              )}

              <hr className="my-3" />

              {/* Profile */}
              <h4 className="font-semibold">
                Profile (Strengths / Limitations)
              </h4>
              {isEditMode ? (
                <UserTaxonomyEditor value={taxo} onChange={setTaxo} />
              ) : (
                <div className="mt-2 text-xs text-green-600">
                  Save the teacher first. You can set Profile on Edit.
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                  <FaSave />
                  {loading ? 'Saving...' : 'Save Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Error Modal */}
      {showImportErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg mb-4 text-red-700">Import Errors</h3>
            <div className="mb-3">
              <span className="text-green-700 font-bold mr-4">
                Imported: {importSuccessCount}
              </span>
              <span className="text-red-700 font-bold">Failed: {importFailCount}</span>
            </div>
            <ul className="text-xs md:text-sm space-y-2 max-h-80 overflow-y-auto">
              {importErrors.map((err, i) => (
                <li key={i} className="border-b pb-1 mb-1">
                  <span className="font-semibold text-red-600">Error:</span>{' '}
                  {err.error}
                  <br />
                  <span className="text-gray-700">{JSON.stringify(err.row)}</span>
                </li>
              ))}
            </ul>
            <button
              className="mt-4 bg-gray-300 px-4 py-2 rounded"
              onClick={() => setShowImportErrorModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {activeTeacherForAudit && !modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[90%] max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Change History</h3>
            <ul className="text-sm space-y-2 max-h-64 overflow-y-auto pr-2">
              {auditLog.length === 0 ? (
                <li className="text-gray-500 italic">No history found.</li>
              ) : (
                auditLog.map((log) => (
                  <li key={log.id} className="text-gray-700">
                    <strong>{log.user_name || 'Unknown'}</strong> – {log.action} –{' '}
                    {String(log.created_at)
                      .replace('T', ' ')
                      .replace('.000Z', '')}
                  </li>
                ))
              )}
            </ul>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setActiveTeacherForAudit(null);
                  setAuditLog([]);
                }}
                className="bg-gray-200 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map modal */}
      {mapModalOpen && mapTeacherLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-[90%] max-w-2xl h-[80vh] relative">
            <h3 className="text-lg font-semibold mb-2">
              Last Location{' '}
              {mapTeacherLocation.name && (
                <span className="text-gray-500">
                  ({mapTeacherLocation.name})
                </span>
              )}
            </h3>
            <div className="w-full h-[65vh] rounded">
              <MapContainer
                center={[mapTeacherLocation.lat, mapTeacherLocation.lon]}
                zoom={15}
                scrollWheelZoom={false}
                style={{
                  width: '100%',
                  height: '60vh',
                  borderRadius: '12px',
                }}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker
                  position={[mapTeacherLocation.lat, mapTeacherLocation.lon]}
                  icon={teacherIcon}
                >
                  <Popup>{mapTeacherLocation.name}</Popup>
                </Marker>
              </MapContainer>
            </div>
            <button
              onClick={() => setMapModalOpen(false)}
              className="absolute top-2 right-2 bg-gray-200 px-3 py-1 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Magic login confirm modal */}
      {loginModalOpen && selectedTeacherForLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[90%] max-w-md">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <FaKey className="text-amber-600" />
              Send login link & QR code
            </h3>
            <p className="text-sm mb-2">
              Do you want to send a secure login link and QR code to:
            </p>
            <p className="font-semibold mb-1">
              {selectedTeacherForLogin.name}
            </p>
            <p className="text-xs text-gray-600 mb-4">
              {selectedTeacherForLogin.email}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              The teacher will receive an email with a one-time magic link and QR code that logs
              them into EarlyCare without sharing a password over the phone.
            </p>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => {
                  setLoginModalOpen(false);
                  setSelectedTeacherForLogin(null);
                }}
                className="bg-gray-200 px-4 py-2 rounded flex items-center gap-2 text-sm"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={handleSendLoginLink}
                disabled={sendingLogin}
                className="bg-amber-600 text-white px-4 py-2 rounded flex items-center gap-2 text-sm"
              >
                <FaKey />
                {sendingLogin ? 'Sending...' : 'Send link & QR'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTeachers;





